import React, { useEffect, useMemo, useState } from 'react';
import CardCommon from '@op/components/CardCommon';
import { RightInfo } from '@op/components/TableExpandableDetail/RightInfo';
import { LanguageType } from '@op/types/common';
import { useRequestStats } from '@op/pages/feeds/hooks/useFeedsRequest';
import ReviewsModal from '@op/components/ReviewsModal';
import useReviewModalData from '@op/components/ReviewsModal/useReviewModalData';
import { transLanguage } from '@op/utils';
import { useIntl } from 'umi';
import { summaryTopicPerformanceTips } from '@op/components/BubbleCommon';
import PositiveAndNegative from './PositiveAndNegative';
import { Keyword, OverviewChart } from '@op/components/ExpandablePlane';
import GPTPanelModal from '@op/components/TableExpandableDetail/GPTPanelModal';
import { SentimentTypeMapTab } from '../../config';

export default function TopicAnalysis({
  topics,
  filter,
  transTopic,
  language,
  topicLevel = 'L1',
  currentTopic,
  sentimentType,
}: {
  topics: Array<string>;
  transTopic: any;
  filter: any;
  language: LanguageType;
  topicLevel?: 'L1' | 'L2';
  currentTopic: string;
  goToFeeds?: () => void;
  sentimentType: string;
}) {
  const { formatMessage } = useIntl();
  const [showAigcWithoutDialog, setShowAigcWithoutDialog] = useState(true);

  const params = useMemo(() => ({
    ...filter,
    topic: topics,
  }), [filter]);

  const { feedsStats, requestState } = useRequestStats({});
  const sentimentCountMap = useMemo(() => {
    const sentimentCountMap = {
      positive: feedsStats?.good_total || 0,
      neutral: feedsStats?.neutral_total || 0,
      negative: feedsStats?.bad_total || 0,
    };

    return sentimentCountMap;
  }, [feedsStats]);

  useEffect(() => {
    requestState(params);
  }, [params]);

  const { reviewsVisible, data, openModal, toggleModal } = useReviewModalData();

  return (
    <CardCommon
      contentCard
      title={formatMessage({ id: 'Topic Analysis' })}
      className="dbo-topic-analysis-card card-slot"
    >
      <div className="dbo-topic-analysis">
        <div className="dbo-topic-analysis-left">
          <PositiveAndNegative
            filter={params}
            topicLevel={topicLevel}
            topics={topics}
            currentTopic={currentTopic}
            sentimentType={sentimentType}
          ></PositiveAndNegative>
          <OverviewChart
            filter={params}
            type="language"
            title="Language"
            translate={(text: string) => transLanguage(text)}
          ></OverviewChart>
          <OverviewChart
            filter={params}
            type="channel"
            title="Channel"
          ></OverviewChart>
        </div>
        <div className="dbo-topic-analysis-right">
          <Keyword language={language} filter={params}></Keyword>

          <div className="dbo-topic-analysis-gpt">
            <RightInfo
              commonParams={{
                ...params,
                topic_level: topicLevel,
              }}
              sentimentCountMap={sentimentCountMap}
              language={language}
              hasGptPanel
              itemClick={(item) => {
                openModal(item);
              }}
              gptModal="Modal2"
              sortItem="engagement"
              tips={summaryTopicPerformanceTips}
              filterTabId={SentimentTypeMapTab[sentimentType]}
            >
              <GPTPanelModal
                filter={{
                  ...params,
                  topic_level: topicLevel,
                }}
                otherParams={{
                  currentTopic,
                  topicText: transTopic(currentTopic, topicLevel),
                }}
                showAigcWithoutDialog={showAigcWithoutDialog}
                setShowAigcWithoutDialog={setShowAigcWithoutDialog}
              ></GPTPanelModal>
            </RightInfo>
          </div>
        </div>
      </div>
      <ReviewsModal
        visible={reviewsVisible}
        data={data}
        language={language}
        close={toggleModal}
      />
    </CardCommon>
  );
}
